import React from 'react';
import { Menu, Bell, LogOut, X, Sun, Moon } from 'lucide-react';
import Logo from '../Logo';
import FitText from '../common/FitText';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import { authApi, isAdminReduxRole } from '../../services/api';
import { logOut } from '../../store/slices/authSlice';
import { toggleTheme } from '../../store/slices/themeSlice';
import { useSidebarStore } from '../../store/sidebarStore';
import { logAuditEvent, AUDIT_ACTIONS } from '../../utils/auditLogger';

function displayNameForUser(user: RootState['auth']['user']): string {
  if (!user) return 'Guest';
  const first =
    user.firstName ||
    (user as { firstname?: string }).firstname;
  const last =
    user.lastName ||
    (user as { lastname?: string }).lastname;
  const full = [first, last].filter(Boolean).join(' ').trim();
  if (full) {
    return full
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
  if (user.username) return user.username;
  if (user.email) {
    const local = user.email.split('@')[0] || '';
    if (!local) return 'Guest';
    return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
  }
  return 'Guest';
}

export default function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDark = useSelector((state: RootState) => state.theme.isDark);
  const user = useSelector((state: RootState) => state.auth.user);
  const branding = useSelector((state: RootState) => state.branding);
  const { isOpen, toggle } = useSidebarStore();

  const displayName = displayNameForUser(user);

  const roleText = isAdminReduxRole(user?.roles) ? 'Admin' : 'Viewer';

  const handleLogout = async () => {
    try {
      // Log logout attempt
      await logAuditEvent(
        AUDIT_ACTIONS.USER_LOGOUT, 
        `User logged out - Company: ${user?.companyName}, User: ${displayNameForUser(user)}`,
        user
      );
      
      await authApi.logout();
      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if the API call fails, we should still clear local state and redirect
      dispatch(logOut());
      window.location.href = '/login';
    }
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  return (
    <header className="bg-app-panel dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between items-center relative">
          <div className="flex items-center min-w-0 flex-1 mr-1">
            <button 
              onClick={toggle}
              className="shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <div className="ml-2 sm:ml-4 flex items-center min-w-0 flex-1">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt="Company logo"
                  className="h-10 sm:h-12 lg:h-14 w-auto max-w-[120px] sm:max-w-[180px] lg:max-w-[220px] mr-2 sm:mr-4 shrink-0 object-contain bg-app-inset rounded p-1"
                />
              ) : (
                <Logo className="h-6 w-auto mr-2 shrink-0 text-[var(--brand-color)]" />
              )}
              <div className="min-w-0 flex-1">
                <FitText
                  as="h1"
                  text={branding.companyName || user?.companyName || 'Monitoring System'}
                  className="font-bold leading-snug text-gray-900 dark:text-white"
                  minPx={11}
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block truncate">{user?.companyName}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <button className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hidden sm:block">
              <Bell className="h-6 w-6" />
            </button>
            <button
              onClick={handleThemeToggle}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
            </button>
            <div className="flex items-center">
              <div className="mr-3 text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Hello, {displayName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{roleText}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Sign Out"
                >
                  <LogOut className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}